/*
 * Overview Level 2 and the session drill-down: the "Download CSV" columns (to_csv shape). Pure.
 * Headers follow the on-screen ones; the money ledger splits into one column per figure, and the
 * teacher / session uuids ride along even where the screen hides them.
 */
import { csv_file_name } from '../../../util_components/csv_export/to_csv';

const PAGE = 'teacher-payments-overview';
const UNATTRIBUTED_LABEL = 'Penalties not linked to a session type';

export const overview_csv_name = (table, pay_month, now) =>
  csv_file_name({ page: PAGE, table, period: pay_month, now });

const teacher_col = (teacher_uuid) => ({ header: 'Teacher UUID', value: () => teacher_uuid });

export const l2_summary_csv_columns = (teacher_uuid) => [
  teacher_col(teacher_uuid),
  { header: 'Category', value: (r) => (r.is_unattributed ? UNATTRIBUTED_LABEL : r.label) },
  { header: 'Category code', key: 'category' },
  {
    header: 'Sessions',
    value: (r) => (r.is_unattributed ? null : r.sessions_count),
    kind: 'number'
  },
  { header: 'Amount', value: (r) => (r.is_unattributed ? null : r.amount), kind: 'money' },
  { header: 'Penalty', key: 'penalty_amount', kind: 'money' },
  { header: 'Waivers', key: 'waived_amount', kind: 'money' },
  { header: 'Corrections', key: 'correction_amount', kind: 'money' },
  { header: 'Free Pass', key: 'free_pass_amount', kind: 'money' },
  { header: 'Uncharged orphans', key: 'uncharged_orphan_amount', kind: 'money' },
  { header: 'Net', value: (r) => (r.is_unattributed ? null : r.net_amount), kind: 'money' }
];

export const l2_incentive_csv_columns = (teacher_uuid) => [
  teacher_col(teacher_uuid),
  { header: 'Category', key: 'category_label' },
  { header: 'Category code', key: 'category' },
  { header: 'Type', key: 'item_type' },
  { header: 'Amount', key: 'amount', kind: 'money' },
  { header: 'Related session', key: 'session_label' },
  { header: 'Session UUID', key: 'session_uuid' }
];

const dash_blank = (v) => (v === '—' ? '' : v);

// Shared by the L2 "Additional payments" card and the ADDITIONAL drill-down (same entry view).
export const additional_entry_csv_columns = (teacher_uuid) => [
  teacher_col(teacher_uuid),
  { header: 'Entry ID', key: 'uuid' },
  { header: 'Type', key: 'type_label' },
  { header: 'Date / time (IST)', key: 'date_text', kind: 'datetime' },
  { header: 'Category', key: 'category_label' },
  { header: 'Ref', key: 'reference_id' },
  { header: 'Source', value: (a) => dash_blank(a.source_text) },
  { header: 'Amount', key: 'amount', kind: 'money' },
  { header: 'Why (rule)', value: (a) => (a.explain && a.explain.rule_ref) || '' },
  { header: 'Note', key: 'note' },
  { header: 'Added by', value: (a) => dash_blank(a.added_by) },
  { header: 'Related session', key: 'session_label' },
  { header: 'Session UUID', key: 'session_uuid' }
];

const OPERATION = { '+': 'Add', '-': 'Subtract', '=': 'Equals' };

// Amount is the line as sent; Signed amount is its effect on payable (a subtracted line negative).
export const final_payable_csv_columns = (teacher_uuid) => [
  teacher_col(teacher_uuid),
  { header: 'Line', key: 'label' },
  { header: 'Line key', key: 'key' },
  { header: 'Operation', value: (l) => OPERATION[l.sign] || '' },
  { header: 'Amount', key: 'amount', kind: 'money' },
  {
    header: 'Signed amount',
    value: (l) =>
      l.amount === null || l.amount === undefined
        ? null
        : l.sign === '-' && l.amount > 0
          ? -l.amount
          : l.amount,
    kind: 'money'
  },
  { header: 'Informational', key: 'is_informational', kind: 'bool' },
  { header: 'Status', value: (l) => (l.amount === null ? l.amount_text : '') }
];

export const history_csv_columns = (teacher_uuid) => [
  teacher_col(teacher_uuid),
  { header: 'When (IST)', key: 'created_at', kind: 'datetime' },
  { header: 'Trigger', value: (h) => dash_blank(h.action) },
  { header: 'Actor', value: (h) => dash_blank(h.actor) },
  { header: 'Reason', value: (h) => dash_blank(h.reason) },
  { header: 'Net before', key: 'net_before', kind: 'money' },
  { header: 'Net after', key: 'net_after', kind: 'money' }
];

// One CSV line per drill-down line (the screen groups a session's lines; the file keeps each).
export const drilldown_item_csv_columns = (teacher_uuid, show_payment = true) =>
  [
    { header: 'Teacher UUID', value: (r) => r.teacher_uuid || teacher_uuid },
    { header: 'Time (IST)', value: (r) => dash_blank(r.time_label), kind: 'datetime' },
    { header: 'Session', key: 'session_label' },
    { header: 'Session UUID', key: 'session_uuid' },
    { header: 'Penalty ID', key: 'penalty_uuid' },
    { header: 'Status', value: (r) => dash_blank(r.status_label) },
    { header: 'Type', key: 'type_label' },
    { header: 'Rule', value: (r) => (r.rule_ref ? r.rule_ref.text.replace(/^Rule: /, '') : '') },
    { header: 'Rule key', value: (r) => (r.rule_ref ? r.rule_ref.rule_key : '') },
    { header: 'Duration', value: (r) => dash_blank(r.duration_label) },
    show_payment ? { header: 'Payment', key: 'payment', kind: 'money' } : null,
    { header: 'Incentive', key: 'incentive', kind: 'money' },
    { header: 'Penalty', key: 'penalty', kind: 'money' },
    { header: 'Penalty corrected to', key: 'corrected_amount', kind: 'money' },
    { header: 'Waived', key: 'waived_amount', kind: 'money' },
    { header: 'Free Pass', key: 'free_pass_amount', kind: 'money' },
    { header: 'Uncharged orphan', key: 'uncharged_orphan_amount', kind: 'money' },
    {
      header: 'Orphaned',
      value: (r) => (r.kind === 'penalty' ? r.is_orphaned : null),
      kind: 'bool'
    },
    { header: 'Note', key: 'note' }
  ].filter(Boolean);
